import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  get,
} from 'lodash';
import {
  Link,
} from 'react-router';
import CircularProgress from '@mui/material/CircularProgress';
import VibeTooltip from '../VibeTooltip/VibeTooltip';
import VibeIcon from '../VibeIcon/VibeIcon';
import {
  transparent,
} from '../../utils/ColorUtil';
import * as rootColor from '../../sass/color.scss';
import './VibeButton.scss';

// Loading timeout to prevent enabling too soon
let loadingTimeout;

/**
 * Get the button theme color palette
 */
function getButtonColors(color, variant) {
  switch (color) {
    case 'primary':
      return {
        backgroundColor: rootColor.primaryButtonBackground,
        textColor: rootColor.primaryButtonText,
      };

    case 'success': {
      if (variant === 'text' || variant === 'outlined') {
        return {
          backgroundColor: rootColor.success,
          textColor: rootColor.success,
        };
      }

      return {
        backgroundColor: rootColor.success,
        textColor: rootColor.white,
      };
    }

    case 'warning': {
      if (variant === 'text' || variant === 'outlined') {
        return {
          backgroundColor: rootColor.warning,
          textColor: rootColor.warning,
        };
      }

      return {
        backgroundColor: rootColor.warning,
        textColor: rootColor.primary,
      };
    }

    case 'error': {
      if (variant === 'text' || variant === 'outlined') {
        return {
          backgroundColor: rootColor.error,
          textColor: rootColor.error,
        };
      }

      return {
        backgroundColor: rootColor.error,
        textColor: rootColor.white,
      };
    }

    case 'gray': {
      if (variant === 'text' || variant === 'outlined') {
        return {
          backgroundColor: rootColor.gray300,
          textColor: rootColor.gray700,
        };
      }

      return {
        backgroundColor: rootColor.gray300,
        textColor: rootColor.primary,
      };
    }

    case 'secondary':
    default: {
      if (variant === 'text' || variant === 'outlined') {
        return {
          backgroundColor: rootColor.secondaryButtonBackground,
          textColor: rootColor.secondaryButtonBackground,
        };
      }

      return {
        backgroundColor: rootColor.secondaryButtonBackground,
        textColor: rootColor.secondaryButtonText,
      };
    }
  }
}

/**
 * Get the button style based on the variant
 * hover state, and color
 */
function getButtonStyle({
  variant = 'contained',
  color,
  hover,
  disabled,
}) {
  // get the colors to use for the button
  const {
    backgroundColor,
    textColor,
  } = getButtonColors(color, variant);

  // styles applied to every button
  const globalStyle = {
    color: textColor,
    fontWeight: 'bold',
  };

  switch (variant) {
    case 'text': {
      const style = {
        ...globalStyle,
        backgroundColor: 'transparent',
        textTransform: 'uppercase',
      };

      if (hover && !disabled) {
        style.backgroundColor = transparent(backgroundColor, 16);
      }

      return style;
    }

    case 'outlined': {
      const style = {
        ...globalStyle,
        backgroundColor: 'transparent',
        border: `1px solid ${backgroundColor}`,
      };

      if (hover && !disabled) {
        style.backgroundColor = transparent(backgroundColor, 16);
      }

      return style;
    }

    case 'contained':
    default: {
      const style = {
        ...globalStyle,
        backgroundColor: transparent(backgroundColor, 80),
        border: `1px solid ${backgroundColor}`,
      };

      if (hover && !disabled) {
        style.backgroundColor = transparent(backgroundColor, 70);
      }

      return style;
    }
  }
}

function VibeButton({
  className = '',
  style = {},
  text,
  variant = 'contained',
  height = 32,
  color = 'primary',
  icon = null,
  iconProps = {},
  tooltip = '',
  tooltipProps = {
    className: '',
    placement: 'bottom',
  },
  disabled: propDisabled = false,
  link = null,
  name = null,
  loadingEvent = null,
  testId,
  onClick = () => {},
}) {
  const [hover, setHover] = useState(false);
  const [loading, setLoading] = useState(false);

  const disabled = propDisabled || loading;

  const buttonStyle = getButtonStyle({
    variant,
    color,
    hover,
    disabled,
  });

  const textStyle = {};

  // when loading, hide the text but don't remove it so the button width doesn't change
  if (loading) {
    textStyle.color = 'transparent';
  }

  const iconPlacement = get(iconProps, 'placement', 'left');
  const iconColor = get(iconProps, 'color', buttonStyle.color);
  const iconSize = get(iconProps, 'size', 16);
  const iconStyle = get(iconProps, 'style', {});

  const onMouseEnter = () => {
    setHover(true);
  };

  const onMouseLeave = () => {
    setHover(false);
  };

  const onClickButton = (e) => {
    onClick(e, name);
  };

  /**
   * Loading event has started
   */
  const startLoading = () => {
    setLoading(true);
  };

  /**
   * Loading event has stopped
   */
  const stopLoading = () => {
    loadingTimeout = setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const buttonHtml = (
    <VibeTooltip
      title={tooltip}
      placement={get(tooltipProps, 'placement', 'bottom')}
      arrow
    >
      <div
        className={classNames('VibeButton', className, { disabled })}
        style={{
          height,
          ...buttonStyle,
          ...style,
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={!disabled
          ? onClickButton
          : null}
        data-testid={testId}
      >
        {icon && iconPlacement === 'left' && (
          <VibeIcon
            className="icon-left"
            style={iconStyle}
            icon={icon}
            color={iconColor}
            size={iconSize}
          />
        )}

        <div
          className={classNames('text', { 'variant-text': variant === 'text' })}
          style={textStyle}
        >
          {text}
        </div>

        {icon && iconPlacement === 'right' && (
          <VibeIcon
            className="icon-right"
            style={iconStyle}
            icon={icon}
            color={iconColor}
            size={iconSize}
          />
        )}

        {loading && (
          <CircularProgress
            color="inherit"
            size={height / 2}
            sx={{
              position: 'absolute',
              zIndex: 1,
            }}
          />
        )}
      </div>
    </VibeTooltip>
  );

  /**
   * Listen for loading events
   */
  useEffect(() => {
    if (!loadingEvent) {
      // no event to listen for
      return;
    }

    document.addEventListener(`${loadingEvent}Start`, startLoading);
    document.addEventListener(`${loadingEvent}Error`, stopLoading);
    document.addEventListener(loadingEvent, stopLoading);

    return () => {
      clearTimeout(loadingTimeout);
      document.removeEventListener(`${loadingEvent}Start`, startLoading);
      document.removeEventListener(`${loadingEvent}Error`, stopLoading);
      document.removeEventListener(loadingEvent, stopLoading);
    };
  }, []);

  return link && !disabled ? (
    <Link to={link}>
      {buttonHtml}
    </Link>
  ) : buttonHtml;
}

VibeButton.propTypes = {
  className: PropTypes.string,
  style: PropTypes.oneOfType([
    PropTypes.object,
  ]),
  text: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string,
  ]).isRequired,
  variant: PropTypes.oneOf([
    'text',
    'contained',
    'outlined',
    'full',
  ]),
  /** Height of the button */
  height: PropTypes.number,
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'success',
    'warning',
    'error',
    'gray',
    'custom',
  ]),
  icon: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.node,
  ]),
  iconProps: PropTypes.shape({
    placement: PropTypes.oneOf([
      'left',
      'right',
    ]),
    color: PropTypes.string,
    size: PropTypes.number,
    style: PropTypes.oneOfType([
      PropTypes.object,
    ]),
  }),
  tooltip: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  tooltipProps: PropTypes.shape({
    className: PropTypes.string,
    placement: PropTypes.string,
  }),
  disabled: PropTypes.bool,
  link: PropTypes.string,
  /** Name to send with onClick event */
  name: PropTypes.string,
  /** Change the icon to a loading spinner when actions are performed */
  loadingEvent: PropTypes.string,
  /** data-testid attribute for E2E test selectors */
  testId: PropTypes.string,
  onClick: PropTypes.func,
};

export default VibeButton;
